import React from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../Components/ui/card";
import { Button } from "../Components/ui/button";
import { Badge } from "../Components/ui/badge";
import { useLanguage } from "../contexts/LanguageContext";
import {
  Cloud,
  BarChart3,
  Brain,
  Shield,
  Code,
  Database,
  ArrowRight,
  CheckCircle
} from "lucide-react";

const solutionDefinitions = [
  { icon: Cloud, color: "emerald" },
  { icon: BarChart3, color: "green" },
  { icon: Brain, color: "teal" },
  { icon: Shield, color: "emerald" },
  { icon: Code, color: "green" },
  { icon: Database, color: "teal" },
];

export default function Solutions() {
  const { t } = useLanguage();
  const copy = t("solutions");

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section id="solutions-hero" className="py-20 bg-gradient-to-br from-emerald-50 to-green-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
            <span className="text-gradient">{copy.hero.titleLead}</span><br />
            {copy.hero.titleTail}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            {copy.hero.body}
          </p>
          <Link to="/contact" className="inline-flex">
            <Button size="lg" className="gradient-green text-white hover:opacity-90">
              {copy.hero.cta}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Kylendar V2 Highlight */}
      <section className="py-12 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 animate-fade-in" style={{ animationDelay: '0.2s' }}>
          <div className="p-1 rounded-3xl bg-gradient-to-r from-emerald-100 via-teal-100 to-blue-100 shadow-xl shadow-emerald-900/5 hover:shadow-2xl hover:shadow-emerald-900/10 transition-shadow duration-500">
            <div className="bg-white/80 backdrop-blur-xl rounded-[22px] p-8 md:p-12 border border-white flex flex-col md:flex-row items-center justify-between gap-8 group">
              <div className="text-center md:text-left flex-1">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-slate-100 text-slate-800 text-xs font-semibold tracking-wider uppercase mb-4 border border-slate-200">
                  <span className="text-emerald-500 mr-2 text-base leading-none">✨</span>
                  {copy.flagship.label}
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3 tracking-tight group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-emerald-700 group-hover:to-teal-700 transition-colors duration-300">
                  {copy.flagship.title}
                </h2>
                <p className="text-slate-600 text-lg max-w-2xl">
                  {copy.flagship.body}
                </p>
              </div>
              <div className="shrink-0 w-full md:w-auto flex flex-col gap-3">
                <a
                  href="https://kylendar.intelevia.com/us/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex md:inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-white transition-all duration-300 rounded-2xl bg-slate-900 hover:bg-gradient-to-r hover:from-emerald-600 hover:to-teal-600 hover:-translate-y-1 hover:shadow-lg hover:shadow-emerald-500/30 overflow-hidden relative"
                >
                  <div className="absolute inset-0 bg-white/20 translate-y-full group-hover/btn:translate-y-0 transition-transform duration-300 ease-out"></div>
                  <span className="relative z-10 flex items-center">
                    {copy.flagship.cta}
                    <ArrowRight className="w-5 h-5 ml-2 transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge variant="outline" className="mb-4 border-emerald-600 text-emerald-600">
              {copy.grid.eyebrow}
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              {copy.grid.title}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {copy.grid.body}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {copy.items.map((solution, index) => {
              const definition = solutionDefinitions[index];
              return (
              <Card key={index} className="border-0 shadow-lg hover-lift group">
                <CardHeader className="pb-4">
                  <div className={`w-14 h-14 bg-${definition.color}-100 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <definition.icon className={`w-7 h-7 text-${definition.color}-600`} />
                  </div>
                  <CardTitle className="text-xl font-bold text-gray-900">
                    {solution.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {solution.description}
                  </p>

                  <div className="space-y-3 mb-6">
                    {solution.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center text-sm">
                        <CheckCircle className="w-4 h-4 text-emerald-600 mr-2 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div className="border-t pt-4">
                    <span className="font-semibold text-emerald-600">
                      {solution.pricing}
                    </span>
                  </div>
                </CardContent>
              </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 gradient-green">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {copy.custom.title}
          </h2>
          <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto">
            {copy.custom.body}
          </p>
          <div className="flex justify-center">
            <Link to="/contact" className="inline-flex">
              <Button size="lg" variant="outline" className="bg-white text-emerald-600 hover:bg-gray-50">
                {copy.custom.cta}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
